import * as React from 'react';
import { createContext, useContext, useMemo } from 'react';

import { useLayer } from '@coursera-web/statsig';

import { CoachStateProvider } from 'bundles/unified-common/providers/CoachStateProvider';
import { TwoPanelChatRouteProvider } from 'bundles/unified-common/providers/TwoPanelChatRouteProvider';
import { useIsCourseDataLoading } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import { useIsS12nDataLoading } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import useIsDesktop from 'bundles/unified-description-page-common/hooks/useIsDesktop';

const LAYER_NAME = 'consumer-xdp-two-panel-layer';
const PARAM_NAME = 'chat-panel-location';
const CHAT_ON_LEFT = 'left';
const CHAT_ON_RIGHT = 'right';
const NO_CHAT_PANEL = 'none';

export type TwoPanelConversationalDiscoveryExperimentContextType = {
  twoPanelConversationalDiscoveryEnabled: boolean;
  chatOnLeft: boolean;
  chatOnRight: boolean;
};

export const TwoPanelConversationalDiscoveryExperimentContext =
  createContext<TwoPanelConversationalDiscoveryExperimentContextType>({
    twoPanelConversationalDiscoveryEnabled: false,
    chatOnLeft: false,
    chatOnRight: false,
  });

function TwoPanelConversationalDiscoveryExperimentProvider({ children }: React.PropsWithChildren) {
  const isS12nDataLoading = useIsS12nDataLoading();
  const isCourseDataLoading = useIsCourseDataLoading();

  const isDesktop = useIsDesktop(); // UA-seeded → SSR↔CSR-stable for the >=1024 layout

  const layer = useLayer(LAYER_NAME);
  const rawLocation = layer.get(PARAM_NAME, NO_CHAT_PANEL);
  const location = isDesktop ? rawLocation : NO_CHAT_PANEL;

  const value = useMemo(() => {
    const chatOnLeft = location === CHAT_ON_LEFT;
    const chatOnRight = location === CHAT_ON_RIGHT;
    return {
      twoPanelConversationalDiscoveryEnabled: chatOnLeft || chatOnRight,
      chatOnLeft,
      chatOnRight,
    };
  }, [location]);

  return (
    <TwoPanelConversationalDiscoveryExperimentContext.Provider value={value}>
      {value.twoPanelConversationalDiscoveryEnabled ? (
        <CoachStateProvider coachOpen={true} isContextConfigLoading={isS12nDataLoading || isCourseDataLoading}>
          <TwoPanelChatRouteProvider>{children}</TwoPanelChatRouteProvider>
        </CoachStateProvider>
      ) : (
        children
      )}
    </TwoPanelConversationalDiscoveryExperimentContext.Provider>
  );
}

export const useTwoPanelConversationalDiscoveryExperiment = () => {
  return useContext(TwoPanelConversationalDiscoveryExperimentContext);
};

export default TwoPanelConversationalDiscoveryExperimentProvider;
